import { createFileRoute } from "@tanstack/react-router";
import { downloadBlob } from "common/utils/downloadFile";
import { EnvPreset } from "gfx/EnvPreset";
import { envPresetSceneLights } from "gfx/sceneLights";
import { DoubleSidedPlaneGeometry } from "gfx/threejs/higharc/DoubleSidedPlaneGeometry";
import { RenderParamsSource } from "gfx/threejs/higharc/RenderParamsSource";
import THREEViewer from "gfx/THREEViewer";
import { activeTHREEViewerServices } from "gfx/THREEViewerServices";
import {
  HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH,
  HIGHARC_ACES_FILMIC_DIVISOR_A,
  HIGHARC_ACES_FILMIC_DIVISOR_B,
  HIGHARC_ACES_FILMIC_DIVISOR_C,
} from "gfx/toneMappingConstants";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import withAuthentication from "../../components/withAuthentication";

const defaultCameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 0,
  y: 0,
  z: 500,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 1,
  upZ: 0,
  cameraNear: 5,
  cameraFar: 1000,
};

const MESH_SIZE = 50;
const MESH_SPACING = MESH_SIZE * 1.3;
const MESH_SPACING_Y = MESH_SIZE * 0.6;

let planeGeo: THREE.BufferGeometry, smallPlaneGeo: THREE.BufferGeometry;

const closestNonTonemappedColorGLSL = `
vec3 closestNonTonemappedColor(vec3 colorA, vec3 toneMappedColor) {
  if (length(toneMappedColor) < 0.01 || length(colorA) < 0.01) {
    return vec3(0.0);
  }
  colorA = colorA / length(colorA) * length(toneMappedColor);
  return colorA;
}

`;

function createGeoIfNeeded() {
  if (planeGeo !== undefined) {
    return;
  }
  planeGeo = new DoubleSidedPlaneGeometry(MESH_SIZE, MESH_SIZE * 0.5, 1, 1);
  smallPlaneGeo = new DoubleSidedPlaneGeometry(MESH_SIZE * 0.1, MESH_SIZE * 0.5, 1, 1);
}

const COLORS: THREE.Color[] = [
  new THREE.Color(1, 0, 0),
  new THREE.Color(0, 1, 0),
  new THREE.Color(0, 0, 1),
  new THREE.Color(1, 0.5, 0.5),
  new THREE.Color(0.5, 1, 0.5),
  new THREE.Color(0.5, 0.5, 1),
  new THREE.Color(1, 1, 0),
  new THREE.Color(0, 1, 1),
  new THREE.Color(1, 0, 1),
  new THREE.Color(1, 1, 0.5),
  new THREE.Color(0.5, 1, 1),
  new THREE.Color(1, 0.5, 1),
  new THREE.Color(1, 1, 1),
  new THREE.Color(0.1, 0.1, 0.1),
];

function TonemappingTestPage() {
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<THREEViewer | undefined>(undefined);

  const displayMeshesRef = useRef<THREE.Mesh[] | null>(null);

  useEffect(() => {
    // Note that not loading the env map will result in the placeholder env map being used.
    const sceneLights = {
      ...envPresetSceneLights[EnvPreset.MORNING_HDR],
      interiorAmbientLight: undefined, // disable the interior ambient light
    };

    viewerRef.current = new THREEViewer({
      container: viewerContainerRef.current,
      options: {
        showAxes: false,
        gui: false,
        ssfxRenderer: true,
        animate: true,
        sceneLights,
        debugFeaturesEnabled: true,
        cameraSettings: defaultCameraSettings,
      },
    });

    viewerRef.current.scene.scene.background = new THREE.Color(0x888888);
    if (viewerRef.current.viewerRenderer.hasSSFXRenderer) {
      viewerRef.current.viewerRenderer.ssfxParamsManager?.setRenderParameters({
        renderPipelineParams: {
          useHalfFloatLinearRenderBuffer: false,
        },
        id: 0,
        source: RenderParamsSource.TESTING,
      });
    }

    void renderGradients(COLORS);

    return () => {
      if (viewerRef.current) {
        const viewer = viewerRef.current;
        viewer.scene.clear();
        viewer.dispose();
        viewerRef.current = undefined;
      }
    };
  }, []);

  async function renderGradients(colorList: THREE.Color[]) {
    const viewer = viewerRef.current;

    createGeoIfNeeded();

    // Put the material meshes in a 2D lattice.
    const planesPerRow = 3;
    const rows = Math.ceil(colorList.length / planesPerRow);

    if (displayMeshesRef.current === undefined) {
      displayMeshesRef.current = [];
    }
    const displayMeshes = displayMeshesRef.current;

    const setupMesh = (mesh: THREE.Mesh) => {
      mesh.updateMatrixWorld();
      viewer?.scene.add(mesh);
      displayMeshes?.push(mesh);
    };

    for (let colorIndex = 0, colorL = colorList.length; colorIndex < colorL; ++colorIndex) {
      const baseColor = colorList[colorIndex];

      const texWidth = 256;
      const colorBuffer = new Float32Array(texWidth * 4);
      const MAX_INTENSITY = 5.0;
      const GRADIENT_POWER = 1.5;
      for (let i = 0; i < texWidth; ++i) {
        const t = Math.pow(i / texWidth, GRADIENT_POWER) * MAX_INTENSITY;
        colorBuffer[i * 4] = baseColor.r * t;
        colorBuffer[i * 4 + 1] = baseColor.g * t;
        colorBuffer[i * 4 + 2] = baseColor.b * t;
        colorBuffer[i * 4 + 3] = 1;
      }

      // This marks the point where the color is at 1.0 intensity
      const fullIntensityT = Math.pow(1.0 / MAX_INTENSITY, 1.0 / GRADIENT_POWER);

      const gradientTexture = new THREE.DataTexture(
        colorBuffer,
        texWidth,
        1,
        THREE.RGBAFormat,
        THREE.FloatType,
        THREE.UVMapping,
        THREE.ClampToEdgeWrapping,
        THREE.ClampToEdgeWrapping,
        THREE.LinearFilter,
        THREE.LinearFilter,
        0,
        THREE.NoColorSpace,
      );
      gradientTexture.needsUpdate = true;

      const gradientMaterial = new THREE.MeshBasicMaterial({ map: gradientTexture });
      gradientMaterial.defines = {
        HIGHARC_ACES_FILMIC_DIVISOR_A,
        HIGHARC_ACES_FILMIC_DIVISOR_B,
        HIGHARC_ACES_FILMIC_DIVISOR_C,
        HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH,
      };
      gradientMaterial.onBeforeCompile = (shader) => {
        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <clipping_planes_pars_fragment>",
          `#include <clipping_planes_pars_fragment>
          #include <tonemapping_pars_fragment>
          ${closestNonTonemappedColorGLSL}
          `,
        );
        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <tonemapping_fragment>",
          `vec3 toneMappedColor = ACESFilmicToneMapping(gl_FragColor.rgb);
          vec3 closestBaseColorMultiple = closestNonTonemappedColor(sampledDiffuseColor.rgb, toneMappedColor);
          if (vMapUv.y > 0.25) {
            gl_FragColor.rgb = toneMappedColor;
          } else if (vMapUv.y > 0.02) {
            gl_FragColor.rgb = closestBaseColorMultiple;
          } else {
            gl_FragColor.rgb = distance(closestBaseColorMultiple, toneMappedColor) < 0.05 ? vec3(0) : vec3(1);
          }
          `,
        );
      };

      const posX = ((colorIndex % planesPerRow) - planesPerRow * 0.5 + 0.5) * MESH_SPACING;
      const posY = (Math.floor(colorIndex / planesPerRow) - rows * 0.5 + 0.5) * MESH_SPACING_Y;

      const planeMesh = new THREE.Mesh(planeGeo, gradientMaterial);
      planeMesh.position.set(posX, posY, 0);
      setupMesh(planeMesh);

      const flatMaterial = new THREE.MeshBasicMaterial({ color: baseColor });
      const smallPlaneMesh = new THREE.Mesh(smallPlaneGeo, flatMaterial);
      smallPlaneMesh.position.set(posX - MESH_SIZE * 0.6, posY, 0);
      setupMesh(smallPlaneMesh);

      const fullIntensityMarker = new THREE.Mesh(smallPlaneGeo, flatMaterial);
      fullIntensityMarker.position.set(posX + (fullIntensityT - 0.5) * MESH_SIZE, posY, -0.1);
      fullIntensityMarker.scale.set(0.1, 1.1, 0.1);
      setupMesh(fullIntensityMarker);
    }
    viewer?.markRendererForUpdate();
  }

  return (
    <div>
      <Head>
        <title>{getPageTitle("Tonemapping Test")}</title>
        <meta name="robots" content="noindex" />

        <style>{`
          body {
              width:  100%;
              height: 100%;
              margin: 0;
              padding: 0;
          }

          .row, .col { overflow: hidden; position: absolute; }
          .row { left: 0; right: 0; }
          .col { top: 0; bottom: 0; }

          .button-container {
            z-index: 100;
            position: absolute;
            bottom: 0;
            left: 0;
            padding: 10px;
          }

          button {
              display: inline-block;
              padding: 5px;
              margin: 5px !important;
          }
        `}</style>
      </Head>

      <div className="row col" ref={viewerContainerRef} />
      <div className="button-container">
        Each gradient shows a single color with varying magnitude. The top section of each gradient is tonemapped, and
        the bottom section shows the multiple of the base color that&apos;s closest to the tonemapped color. The flat
        rectangle next to each gradient shows the base color without tonemapping. The line crossing the gradient marks
        the part where the base color is at intensity 1.0 in the gradient. The values after that are higher intensity
        colors. The white line at the bottom of the gradient shows the part where the RGB space distance between the two
        colors being compared is more than 0.05.
        <button
          onClick={() => {
            const viewer = viewerRef.current;
            if (!viewer || !viewer.viewerRenderer.ssfxParamsManager || !viewer.scene.ssfxRendererScene) {
              return;
            }
            const renderWidth = 4096;
            const renderHeight = 2048;
            activeTHREEViewerServices.imageRenderer.renderImageSync(
              viewer.scene.ssfxRendererScene,
              viewer.camera,
              viewer.viewerRenderer.ssfxParamsManager.getRenderParams(),
              { width: renderWidth, height: renderHeight, ssaaSamples: 16 },
              (img: CanvasImageSource) => {
                const canvas = document.createElement("canvas");
                canvas.width = renderWidth;
                canvas.height = renderHeight;
                canvas.getContext("2d")?.drawImage(img, 0, 0);
                canvas.toBlob((blob) => {
                  if (blob) {
                    downloadBlob("tonemapping-test.png", blob);
                  }
                }, "image/png");
              },
            );
          }}
        >
          Generate high-res PNG
        </button>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/debug/tonemapping-test")({
  component: withAuthentication(TonemappingTestPage, { superAdmin: true }),
});
